import { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { 
  ArrowLeft, 
  ArrowRight, 
  MapPin, 
  CheckCircle2, 
  Check,
  Map,
  Info,
  Users,
  Gavel,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../lib/utils";
import { useToast } from "../hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "../lib/api";

// Fix Leaflet default icon issue
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

// Map updater component to handle center changes
function MapUpdater({ center, zoom }: { center: [number, number]; zoom: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
}

// --- Schemas ---

const basicDetailsSchema = z.object({
  propertyName: z.string().optional(),
  address: z.string().min(5, "Address is required"),
});

const locationCommitteesSchema = z.object({
  municipality: z.string().min(1, "Please select a municipality"),
});

const additionalDetailsSchema = z.object({
  currentUse: z.string().min(1, "Required"),
  proposedUse: z.string().min(1, "Required"),
  description: z.string().optional(),
  legalEntityName: z.string().optional(),
  billingAddress: z.string().optional(),
});

type BasicDetailsValues = z.infer<typeof basicDetailsSchema>;
type LocationCommitteesValues = z.infer<typeof locationCommitteesSchema>;
type AdditionalDetailsValues = z.infer<typeof additionalDetailsSchema>;

type Municipality = { 
  id: number; 
  name: string; 
  parent_id?: number; 
  is_region?: boolean; 
  parent_name?: string; 
};

type Committee = { 
  id: number; 
  municipality: string;
  committee_type_id: number;
  committee_type: { 
    id: number;
    slug: string;
    display_name: string;
  };
};

export default function AddProperty() {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<Partial<BasicDetailsValues & LocationCommitteesValues & AdditionalDetailsValues>>({});
  
  // Data state
  const [monitoredMunicipalities, setMonitoredMunicipalities] = useState<Municipality[]>([]);
  const [committees, setCommittees] = useState<Committee[]>([]);
  const [landUseTypes, setLandUseTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState({
    municipalities: false,
    committees: false,
    landUseTypes: false,
    create: false,
    geocoding: false
  });

  // Error dialog state
  const [errorDialog, setErrorDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
  }>({
    open: false,
    title: "",
    message: ""
  });

  // Fetch trial status
  const { data: trialStatus } = useQuery({
    queryKey: ['trial-status'],
    queryFn: () => apiClient.auth.getTrialStatus(),
  });

  // Map state
  const [mapCoordinates, setMapCoordinates] = useState<[number, number] | null>(null);

  // Address autocomplete state
  const addressBoxRef = useRef<HTMLDivElement | null>(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrSuggestions, setAddrSuggestions] = useState<any[]>([]);
  const [justSelectedAddress, setJustSelectedAddress] = useState(false);

  // Get municipality name from selected municipality ID
  const municipalityName = useMemo(() => {
    const id = parseInt(formData.municipality || "0", 10);
    const muni = monitoredMunicipalities?.find(m => m.id === id);
    return muni?.name?.toLowerCase();
  }, [formData.municipality, monitoredMunicipalities]);

  // Fetch monitored municipalities on mount
  useEffect(() => {
    const fetchMonitoredMunicipalities = async () => {
      try {
        setLoading(prev => ({ ...prev, municipalities: true }));
        const data = await apiClient.companyMunicipalities.listAll();
        const municipalityData = data.map((cm: any) => ({
          id: cm.municipality_id,
          name: cm.municipality_name,
          parent_id: cm.parent_id,
          parent_name: cm.parent_name,
          is_region: cm.is_region
        }));
        setMonitoredMunicipalities(municipalityData);
      } catch (err) {
        console.error('Error fetching monitored municipalities:', err);
        toast({
          title: "Error",
          description: "Failed to load monitored municipalities.",
          variant: "destructive",
        });
      } finally {
        setLoading(prev => ({ ...prev, municipalities: false }));
      }
    };

    fetchMonitoredMunicipalities();
  }, [toast]);

  // Fetch land use types on mount
  useEffect(() => {
    const fetchData = async () => {

      try {
        setLoading(prev => ({ ...prev, landUseTypes: true }));
        const landUseData = await apiClient.properties.getLandUseTypes();
        setLandUseTypes(landUseData);
      } catch (err) {
        console.error('Error fetching land use types:', err);
        toast({
          title: "Error",
          description: "Failed to load land use types.",
          variant: "destructive",
        });
      } finally {
        setLoading(prev => ({ ...prev, landUseTypes: false }));
      }
    };

    fetchData();
  }, [toast]);

  // Fetch committees when municipality changes
  useEffect(() => {
    const fetchCommittees = async () => {
      if (!municipalityName) {
        setCommittees([]);
        return;
      }

      try {
        setLoading(prev => ({ ...prev, committees: true }));
        const committeesData = await apiClient.committees.getByMunicipality(municipalityName);
        setCommittees(committeesData);
      } catch (err) {
        console.error('Error fetching committees:', err);
        setCommittees([]);
      } finally {
        setLoading(prev => ({ ...prev, committees: false }));
      }
    };

    fetchCommittees();
  }, [municipalityName]);

  // Geocode address and update map coordinates
  useEffect(() => {
    const geocodeAddress = async () => {
      const address = formData.address?.trim();
      if (!address || address.length < 3) {
        setMapCoordinates(null);
        return;
      }

      const token = import.meta.env.VITE_MAPBOX_TOKEN;
      if (!token) {
        console.warn('Mapbox token not configured');
        return;
      }

      try {
        setLoading(prev => ({ ...prev, geocoding: true }));
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?country=CA&limit=1&access_token=${token}`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].center;
          setMapCoordinates([lat, lng]);
        } else {
          setMapCoordinates(null);
        }
      } catch (err) {
        console.error('Error geocoding address:', err);
        setMapCoordinates(null);
      } finally {
        setLoading(prev => ({ ...prev, geocoding: false }));
      }
    };

    // Debounce geocoding
    const timeoutId = setTimeout(geocodeAddress, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.address]);

  // Mapbox autocomplete for address suggestions
  useEffect(() => {
    // Don't search if we just selected an address
    if (justSelectedAddress) {
      setAddrSuggestions([]);
      setAddrOpen(false);
      return;
    }
    
    const token = import.meta.env.VITE_MAPBOX_TOKEN;
    const address = formData.address?.trim();
    
    if (!token || !address || address.length < 3) {
      setAddrSuggestions([]);
      setAddrOpen(false);
      return;
    }
    
    setAddrLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?autocomplete=true&types=address,place,locality&country=CA&limit=5&access_token=${token}`;
        const response = await fetch(url);
        const data = await response.json();
        setAddrSuggestions(Array.isArray(data?.features) ? data.features : []);
        setAddrOpen(true);
      } catch (err) {
        console.error('Mapbox autocomplete error:', err);
        setAddrSuggestions([]);
        setAddrOpen(false);
      } finally {
        setAddrLoading(false);
      }
    }, 350);
    
    return () => clearTimeout(timeoutId);
  }, [formData.address, justSelectedAddress]);

  // Close address dropdown on outside click or ESC
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (addressBoxRef.current && !addressBoxRef.current.contains(e.target as Node)) {
        setAddrOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setAddrOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Select address from autocomplete
  const selectAddress = (feature: any) => {
    const placeName = feature?.place_name || feature?.text || formData.address;
    
    // Set the flag first to prevent immediate re-search
    setJustSelectedAddress(true);
    
    // Update form data with selected address
    setFormData(prev => ({ ...prev, address: placeName }));
    
    // Extract city/locality and auto-select municipality if found
    try {
      const context = feature?.context || [];
      const cityObj = context.find((c: any) => (c.id || '').startsWith('place.'))
        || context.find((c: any) => (c.id || '').startsWith('locality.'));
      const city = (cityObj?.text || '').toLowerCase();
      if (city && monitoredMunicipalities && monitoredMunicipalities.length) {
        const match = monitoredMunicipalities.find(m => m.name.toLowerCase() === city);
        if (match) {
          setFormData(prev => ({ ...prev, municipality: String(match.id) }));
        }
      }
    } catch {}
    
    // Close dropdown
    setAddrOpen(false);
    setAddrSuggestions([]);
  };

  // Step Components
  const handleBasicSubmit = (data: BasicDetailsValues) => {
    setFormData(prev => ({ ...prev, ...data }));
    setStep(2);
  };

  const handleLocationSubmit = (data: LocationCommitteesValues) => {
    setFormData(prev => ({ ...prev, ...data }));
    setStep(3);
  };

  const handleFinalSubmit = async (data: AdditionalDetailsValues) => {
    try {
      setLoading(prev => ({ ...prev, create: true }));
      
      const finalData = { ...formData, ...data };
      
      // Map form data to API payload
      const payload: any = {
        name: finalData.propertyName || undefined,
        address: finalData.address || undefined,
        municipality_id: finalData.municipality ? parseInt(finalData.municipality) : undefined,
        current_land_use: finalData.currentUse || undefined,
        future_land_use: finalData.proposedUse || undefined,
        notes: finalData.description || undefined,
        billing_name: finalData.legalEntityName || undefined,
        billing_address: finalData.billingAddress || undefined,
        // Auto-select all committees for the municipality
        selectedCommittees: committees.map(c => c.id),
      };

      // Create the property first
      const property = await apiClient.properties.create(payload);
      const propertyId = property.id;
      let propertyCreated = true; // Track if we need to clean up
      
      // Create subscription directly using existing payment method
      try {
        const subscriptionResult = await apiClient.stripe.subscribeToProperty(propertyId);
        
        // Force immediate refetch of queries so Sources page updates immediately
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ['sources'] }),
          queryClient.refetchQueries({ queryKey: ['all-properties'] })
        ]);
        
        toast({
          title: "Property Added",
          description: subscriptionResult.already_subscribed 
            ? "Property already has an active subscription."
            : "Monitoring subscription activated successfully.",
        });
        
        // Redirect to sources page after data is refreshed
        setTimeout(() => navigate("/sources"), 500);
      } catch (subscriptionError: any) {
        console.error("Subscription error:", subscriptionError);
        
        // If no payment method exists, redirect to checkout (keep property for checkout)
        if (subscriptionError?.response?.status === 400 && 
            subscriptionError?.response?.data?.detail?.includes("No payment method")) {
          const checkoutData = await apiClient.stripe.createPropertyCheckout(propertyId);
          
          if (checkoutData.checkout_url) {
            // Redirect to Stripe checkout to add payment method
            // Property will be cleaned up by webhook if checkout is cancelled
            window.location.href = checkoutData.checkout_url;
            return;
          }
        }
        
        // If subscription fails, delete the property that was just created
        if (propertyCreated && propertyId) {
          try {
            await apiClient.properties.delete(propertyId);
            console.log(`✅ Cleaned up property ${propertyId} after subscription failure`);
          } catch (deleteError) {
            console.error(`⚠️ Failed to cleanup property ${propertyId}:`, deleteError);
            // Continue to show error even if cleanup fails
          }
        }
        
        // Show error message
        setErrorDialog({
          open: true,
          title: "Subscription Error",
          message: subscriptionError?.response?.data?.detail || "Property was created but we couldn't set up the subscription. The property has been removed. Please try again."
        });
        setLoading(prev => ({ ...prev, create: false }));
        return;
      }
    } catch (err: any) {
      console.error("Failed to create property", err);
      
      // Check for duplicate address error (409 Conflict)
      if (err?.response?.status === 409 || 
          (err?.response?.data?.detail && err.response.data.detail.includes("same address"))) {
        setErrorDialog({
          open: true,
          title: "Duplicate Address",
          message: "You cannot add the same address more than once. This address already exists in your properties."
        });
      } else {
        // Show other errors in dialog too for better visibility
        setErrorDialog({
          open: true,
          title: "Error Creating Property",
          message: err?.response?.data?.detail || "Failed to create property. Please try again."
        });
      }
      setLoading(prev => ({ ...prev, create: false }));
    }
  };


  return (
    <div className="min-h-screen w-full flex overflow-hidden">
      {/* Left Side - Form Area */}
      <div className="w-full lg:w-1/2 flex flex-col bg-background relative z-10">
        <div className="flex-1 flex flex-col justify-center items-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-6">
            {/* Back Button */}
            <div className="mb-4">
              <Button 
                variant="ghost" 
                size="sm"
                className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
                onClick={() => navigate("/sources")}
              >
                <ArrowLeft className="h-4 w-4" /> Back to Properties
              </Button>
            </div>
            
            <div className="text-center lg:text-left">
              <h1 className="text-3xl font-bold tracking-tight text-foreground mb-2">
                Add New Property
              </h1>
              <p className="text-muted-foreground mb-6">
                Configure monitoring for a new development site.
              </p>

              {/* Stepper */}
              <div className="flex items-center gap-2 mb-8">
                {[1, 2, 3].map((s) => (
                  <div key={s} className="flex items-center">
                    <div className={cn(
                      "h-2 w-12 rounded-full transition-colors duration-300",
                      step >= s ? "bg-primary" : "bg-neutral-200 dark:bg-neutral-800"
                    )} />
                    {s < 3 && <div className="w-2" />}
                  </div>
                ))}
              </div>

              <AnimatePresence mode="wait" custom={step}>
                {step === 1 && (
                  <motion.div
                    key="step1"
                    custom={1}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <BasicDetailsForm 
                      defaultValues={formData} 
                      onSubmit={handleBasicSubmit}
                      addressBoxRef={addressBoxRef}
                      addrOpen={addrOpen}
                      addrLoading={addrLoading}
                      addrSuggestions={addrSuggestions}
                      selectAddress={selectAddress}
                      justSelectedAddress={justSelectedAddress}
                      setJustSelectedAddress={setJustSelectedAddress}
                      onAddressChange={(value) => {
                        setFormData(prev => ({ ...prev, address: value }));
                        if (!value || value.length < 3) {
                          setJustSelectedAddress(false);
                          setAddrOpen(false);
                          setAddrSuggestions([]);
                        }
                      }}
                    />
                  </motion.div>
                )}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    custom={1}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <LocationCommitteesForm 
                      defaultValues={formData} 
                      onSubmit={handleLocationSubmit} 
                      onBack={() => setStep(1)}
                      monitoredMunicipalities={monitoredMunicipalities}
                      loading={loading.municipalities}
                      onMunicipalityChange={(val) => {
                        setFormData(prev => ({ ...prev, municipality: val }));
                      }}
                    />
                  </motion.div>
                )}
                {step === 3 && (
                  <motion.div
                    key="step3"
                    custom={1}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AdditionalDetailsForm 
                      defaultValues={formData} 
                      onSubmit={handleFinalSubmit} 
                      onBack={() => setStep(2)}
                      landUseTypes={landUseTypes}
                      loading={loading.landUseTypes || loading.create}
                      trialStatus={trialStatus}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Content */}
      <div className="hidden lg:flex w-1/2 relative bg-neutral-50 dark:bg-neutral-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10 dark:opacity-5 pointer-events-none" 
             style={{ backgroundImage: "radial-gradient(#84cc16 1px, transparent 1px)", backgroundSize: "24px 24px" }} 
        />

        <div className="relative z-10 flex flex-col justify-center w-full h-full">
          {step === 1 ? (
            <div className="w-full h-full p-4">
              {/* Map view for step 1 */}
              <div className="w-full h-full rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-800 shadow-lg relative">
                {mapCoordinates ? (
                  <MapContainer
                    center={mapCoordinates}
                    zoom={15}
                    className="w-full h-full z-0"
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapUpdater center={mapCoordinates} zoom={15} />
                    <Marker position={mapCoordinates}>
                    </Marker>
                  </MapContainer>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
                    {loading.geocoding ? (
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-8 w-8 text-primary animate-spin" />
                        <p className="text-sm text-neutral-500">Finding location...</p>
                      </div>
                    ) : formData.address ? (
                      <div className="flex flex-col items-center gap-3 text-center px-8">
                        <MapPin className="h-12 w-12 text-neutral-400" />
                        <p className="text-sm text-neutral-500">Unable to find location for this address</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-3 text-center px-8">
                        <Map className="h-12 w-12 text-neutral-400" />
                        <p className="text-sm text-neutral-500">Enter an address to see it on the map</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-12 space-y-8">
              {/* Step 2: Show Committees Monitoring */}
              {step === 2 ? (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="w-full max-w-lg space-y-6 relative z-10"
              >
                {(() => {
                  const selectedMuni = formData.municipality 
                    ? monitoredMunicipalities.find(m => String(m.id) === formData.municipality)
                    : null;
                  const liveMunicipality = selectedMuni?.name || null;

                  return (
                    <>
                      <div className="text-center mb-8">
                        <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 mb-6">
                          <Gavel className="h-8 w-8 text-primary" />
                        </div>
                        <h2 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
                          {liveMunicipality || "Select Municipality"}
                        </h2>
                        <p className="text-lg text-neutral-500">
                          {liveMunicipality 
                            ? `Automatically monitoring ${committees.length} ${committees.length === 1 ? 'committee' : 'committees'}`
                            : "Select a municipality to view monitored committees"}
                        </p>
                      </div>

                      {liveMunicipality && (
                        <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 shadow-lg overflow-hidden">
                          <div className="px-6 py-4 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 flex items-center justify-between">
                            <span className="font-semibold text-sm text-neutral-900 dark:text-white">Active Committees</span>
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Live Monitoring</Badge>
                          </div>
                          <div className="max-h-[400px] overflow-y-auto p-2">
                            {loading.committees ? (
                              <div className="p-4 text-center text-sm text-neutral-500">Loading committees...</div>
                            ) : committees.length > 0 ? (
                              committees.map((committee) => (
                                <div key={committee.id} className="flex items-center gap-3 p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors">
                                  <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                    <Users className="h-4 w-4" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-neutral-900 dark:text-white">{committee.committee_type.display_name}</div>
                                    <div className="text-xs text-neutral-500">Agendas & Minutes</div>
                                  </div>
                                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                                </div>
                              ))
                            ) : (
                              <div className="p-4 text-center text-sm text-neutral-500">No committees available for this municipality</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Regional Coverage */}
                      {selectedMuni?.parent_name && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2, duration: 0.4 }}
                          className="bg-primary/10 backdrop-blur-sm rounded-xl border border-primary/20 p-4"
                        >
                          <div className="flex items-start gap-3">
                            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-semibold text-white mb-1">
                                Regional Coverage
                              </p>
                              <p className="text-xs text-neutral-300 leading-relaxed">
                                Also monitoring {selectedMuni.parent_name} council meetings for comprehensive coverage.
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </>
                  );
                })()}
              </motion.div>
            ) : (
              <>
                {/* Default view for step 3 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="w-full max-w-lg space-y-6 relative z-10"
                >
                  <h2 className="text-4xl xl:text-5xl font-bold leading-tight mb-6 text-neutral-900 dark:text-white">
                    Monitor Development Sites with Ease
                  </h2>
                  <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-md leading-relaxed">
                    Add properties to track planning applications, meetings, and insights
                    in real-time. Get notified when something important happens.
                  </p>

                  <div className="space-y-4 pt-4">
                    {[
                      "Real-time Planning Alerts",
                      "Automated Meeting Tracking",
                      "Property Insights & Analysis",
                      "Municipality-wide Monitoring",
                    ].map((item, i) => (
                      <motion.div
                        key={item}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                        className="flex items-center gap-3 text-neutral-700 dark:text-neutral-200"
                      >
                        <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-primary border border-primary/30">
                          <Check className="h-3.5 w-3.5" />
                        </div>
                        <span className="font-medium">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </>
            )}
            </div>
          )}
        </div>
      </div>
      
      {/* Error Dialog */}
      <Dialog open={errorDialog.open} onOpenChange={(open) => setErrorDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 dark:text-red-400">{errorDialog.title}</DialogTitle>
            <DialogDescription className="pt-2 text-base">
              {errorDialog.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              onClick={() => setErrorDialog(prev => ({ ...prev, open: false }))}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Sub-Forms ---

function BasicDetailsForm({ 
  defaultValues, 
  onSubmit,
  addressBoxRef,
  addrOpen,
  addrLoading,
  addrSuggestions,
  selectAddress,
  justSelectedAddress,
  setJustSelectedAddress,
  onAddressChange
}: { 
  defaultValues: any, 
  onSubmit: (data: BasicDetailsValues) => void,
  addressBoxRef: React.RefObject<HTMLDivElement | null>,
  addrOpen: boolean,
  addrLoading: boolean,
  addrSuggestions: any[],
  selectAddress: (feature: any) => void,
  justSelectedAddress: boolean,
  setJustSelectedAddress: (value: boolean) => void,
  onAddressChange: (value: string) => void
}) {
  const form = useForm<BasicDetailsValues>({
    resolver: zodResolver(basicDetailsSchema),
    defaultValues: {
      propertyName: defaultValues.propertyName || "",
      address: defaultValues.address || "",
    }
  });

  const addressValue = form.watch("address");

  // Sync form value with parent state when defaultValues change (e.g., after address selection)
  useEffect(() => {
    if (defaultValues.address && defaultValues.address !== addressValue) {
      form.setValue("address", defaultValues.address);
    }
  }, [defaultValues.address, addressValue, form]);

  // Sync form value to parent state when user types
  useEffect(() => {
    if (addressValue && addressValue !== defaultValues.address) {
      onAddressChange(addressValue);
    }
  }, [addressValue, defaultValues.address, onAddressChange]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="propertyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Name (Optional)</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. Glenforest Development" {...field} />
                </FormControl>
                <p className="text-[11px] text-neutral-500">
                  If left blank, the address will be used as the name.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address <span className="text-red-500">*</span></FormLabel>
                <FormControl>
                  <div ref={addressBoxRef} className="relative">
                    <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-neutral-400 z-10 pointer-events-none" />
                    <Input 
                      placeholder="Start typing an address..." 
                      className="pl-9" 
                      {...field}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        field.onChange(e);
                        onAddressChange(newValue);
                        // Reset the flag when user starts typing again
                        if (justSelectedAddress && newValue !== defaultValues.address) {
                          setJustSelectedAddress(false);
                        }
                      }}
                      onFocus={() => {
                        if (!justSelectedAddress && addressValue && addressValue.length >= 3) {
                          // Suggestions will be shown by useEffect
                        }
                      }}
                      onBlur={() => {
                        // Delay closing to allow click on suggestions
                        setTimeout(() => {
                          if (!justSelectedAddress) {
                            setJustSelectedAddress(false);
                          }
                        }, 150);
                      }}
                    />
                    {addrOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-md shadow-lg max-h-64 overflow-y-auto">
                        {addrLoading ? (
                          <div className="px-3 py-2 text-sm text-neutral-500 flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Searching…
                          </div>
                        ) : addrSuggestions.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-neutral-500">
                            {import.meta.env.VITE_MAPBOX_TOKEN === 'your_mapbox_token_here' || !import.meta.env.VITE_MAPBOX_TOKEN 
                              ? "Address autocomplete requires Mapbox token configuration" 
                              : "No suggestions"}
                          </div>
                        ) : (
                          <ul className="py-1">
                            {addrSuggestions.map((f: any) => (
                              <li
                                key={f.id}
                                onMouseDown={(e) => {
                                  e.preventDefault(); // Prevent input blur
                                  form.setValue("address", f.place_name || f.text || "");
                                  selectAddress(f);
                                }}
                                className="px-3 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer"
                              >
                                {f.place_name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button type="submit" className="w-full h-11 text-base font-medium group">
          Continue <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </Button>
      </form>
    </Form>
  );
}

function LocationCommitteesForm({ 
  defaultValues, 
  onSubmit, 
  onBack,
  monitoredMunicipalities,
  loading,
  onMunicipalityChange
}: { 
  defaultValues: any, 
  onSubmit: (data: LocationCommitteesValues) => void, 
  onBack: () => void,
  monitoredMunicipalities: Municipality[],
  loading: boolean,
  onMunicipalityChange: (val: string) => void
}) {
  const form = useForm<LocationCommitteesValues>({
    resolver: zodResolver(locationCommitteesSchema),
    defaultValues: {
      municipality: defaultValues.municipality || "",
    }
  });

  const selectedMunicipality = form.watch("municipality");

  // Update parent state whenever municipality changes
  useEffect(() => {
    if (selectedMunicipality) {
      onMunicipalityChange(selectedMunicipality);
    }
  }, [selectedMunicipality, onMunicipalityChange]);

  // Get selected municipality info for regional coverage
  const selectedMuni = selectedMunicipality 
    ? monitoredMunicipalities.find(m => String(m.id) === selectedMunicipality)
    : null;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-medium leading-none">Location & Committees</h3>
          <FormField
            control={form.control}
            name="municipality"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Municipality</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value} disabled={loading}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={loading ? "Loading municipalities..." : "Select municipality"} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {monitoredMunicipalities.length === 0 ? (
                      <SelectItem value="" disabled>No monitored municipalities - Add one first</SelectItem>
                    ) : (
                      monitoredMunicipalities.map(m => (
                        <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
                {monitoredMunicipalities.length === 0 && (
                  <p className="text-sm text-amber-600 mt-1">
                    You must monitor a municipality before adding properties to it.
                  </p>
                )}
              </FormItem>
            )}
          />

          {selectedMuni?.parent_name && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800 flex gap-3 items-start">
              <Info className="h-5 w-5 shrink-0 text-blue-600 mt-0.5" />
              <div>
                <span className="font-semibold block mb-1">Regional Coverage</span>
                Also monitoring {selectedMuni.parent_name} council meetings for comprehensive coverage.
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-4 mt-auto">
          <Button type="button" variant="outline" onClick={onBack} className="flex-1 h-11">
            Back
          </Button>
          <Button type="submit" className="flex-[2] h-11 text-base font-medium">
            Next Step <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </form>
    </Form>
  );
}

function AdditionalDetailsForm({ 
  defaultValues, 
  onSubmit, 
  onBack,
  landUseTypes,
  loading,
  trialStatus
}: { 
  defaultValues: any, 
  onSubmit: (data: AdditionalDetailsValues) => void, 
  onBack: () => void,
  landUseTypes: string[],
  loading: boolean,
  trialStatus?: any
}) {
  const [showBilling, setShowBilling] = useState(!!defaultValues.legalEntityName);
  
  const form = useForm<AdditionalDetailsValues>({
    resolver: zodResolver(additionalDetailsSchema),
    defaultValues: {
      currentUse: defaultValues.currentUse || "",
      proposedUse: defaultValues.proposedUse || "",
      description: defaultValues.description || "",
      legalEntityName: defaultValues.legalEntityName || "",
      billingAddress: defaultValues.billingAddress || "",
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="currentUse"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Current Land Use</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value} disabled={loading}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={loading ? "Loading..." : "Select..."} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {landUseTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="proposedUse"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Future/Proposed Use</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value} disabled={loading}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={loading ? "Loading..." : "Select..."} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {landUseTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Unit count, height, typology, etc." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="pt-4 border-t">
          <div className="flex items-center justify-between mb-4">
             <Label className="text-base font-medium">Separate Billing Entity</Label>
             <Checkbox checked={showBilling} onCheckedChange={(c) => setShowBilling(!!c)} />
          </div>
          
          {showBilling && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              className="space-y-4 overflow-hidden p-1"
            >
              <p className="text-xs text-neutral-500 mb-2">
                If this property should show a different legal entity on invoices, provide details below.
              </p>
              <FormField
                control={form.control}
                name="legalEntityName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Legal Entity Name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 1234567 Ontario Inc." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="billingAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Address</FormLabel>
                    <FormControl>
                      <Input placeholder="123 Business St, Toronto ON" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>
          )}
        </div>

        {/* Property Pricing Information */}
        <div className="pt-4 border-t">
          {trialStatus?.is_trial_active ? (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3.5 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">🎉</span>
                  <div>
                    <div className="font-semibold text-sm text-green-700 dark:text-green-400">
                      Free During Trial
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-500">
                      {trialStatus.days_remaining} {trialStatus.days_remaining === 1 ? 'day' : 'days'} remaining
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-green-600 dark:text-green-500 font-medium">Trial ends</div>
                  <div className="text-xs text-green-700 dark:text-green-400 font-semibold">
                    {trialStatus.trial_end_date ? new Date(trialStatus.trial_end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  </div>
                </div>
              </div>
              <div className="pt-2 border-t border-green-200 dark:border-green-800 flex items-center justify-between">
                <span className="text-xs text-neutral-600 dark:text-neutral-400">After trial:</span>
                <span className="text-sm font-bold text-neutral-900 dark:text-white">$25.00/month</span>
              </div>
            </div>
          ) : (
            <div className="bg-neutral-50 dark:bg-neutral-900/50 border border-neutral-200 dark:border-neutral-800 rounded-lg p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Property Monitoring</span>
                <span className="text-base font-bold text-neutral-900 dark:text-white">$25.00/month</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onBack} className="flex-1 h-11" disabled={loading}>
            Back
          </Button>
          <Button type="submit" className="flex-[2] h-11 text-base font-medium bg-primary hover:bg-primary/90 text-white" disabled={loading}>
            {loading ? "Creating..." : "Create Property"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
